import { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@app/components/Button/Button';

const menuItems = [
  { id: 'businesses', label: 'Businesses', path: '/admin/businesses' },
  { id: 'dashboard', label: 'Dashboard', path: '/admin' },
  { id: 'users', label: 'Users', path: '/admin/users' },
  { id: 'plans', label: 'Plans', path: '/admin/plans' },
];

const logSubItems = [
  { id: 'activity', label: 'Activity Logs', path: '/admin/logs/activity' },
  { id: 'error', label: 'Error Logs', path: '/admin/logs/error' },
  { id: 'audit', label: 'Audit Logs', path: '/admin/logs/audit' },
  { id: 'reviews-syncs', label: 'Reviews synchronizations', path: '/admin/logs/reviews-syncs' },
];

export const AdminLayout = () => {
  const location = useLocation();

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [logsExpanded, setLogsExpanded] = useState(() => {
    return location.pathname.startsWith('/admin/logs/');
  });
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('googleAccessToken');
    localStorage.removeItem('businessId');
    localStorage.removeItem('userPlanTypes');
    localStorage.removeItem('impersonation');
    localStorage.removeItem('adminToken');
    navigate('/login');
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const isLogRouteActive = () => {
    return location.pathname.startsWith('/admin/logs/');
  };

  const isLogSubItemActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
          <h1 className="text-xl font-bold text-gray-900">Admin Panel</h1>
          <button
            onClick={() => setSidebarOpen(false)}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <nav className="mt-5 px-2">
          <div className="space-y-1">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => navigate(item.path)}
                className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive(item.path)
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                {item.label}
              </button>
            ))}

            {/* Logs Section */}
            <div>
              <button
                onClick={() => setLogsExpanded(!logsExpanded)}
                className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-between ${
                  isLogRouteActive()
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <span>Logs</span>
                <svg
                  className={`w-4 h-4 transition-transform duration-200 ${
                    logsExpanded ? 'transform rotate-90' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>

              {logsExpanded && (
                <div className="ml-4 mt-1 space-y-1">
                  {logSubItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => navigate(item.path)}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        isLogSubItemActive(item.path)
                          ? 'bg-indigo-50 text-indigo-600'
                          : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200">
          <Button
            onClick={() => navigate('/')}
            className="w-full"
            kind="secondary"
          >
            Back to User Dashboard
          </Button>

          <Button
            onClick={handleLogout}
            className="w-full bg-red-600 hover:bg-red-700 text-white mt-4"
          >
            Logout
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div
        className={`transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-0'}`}
      >
        {/* Top bar */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
